<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'InflationIndexedPriceChange',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    id: {
      type: String,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const labelAlign = ref('right');
  const item = ref(null);
  const payload = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'md',
  }));

  const $data = computed(() => ({
    labelAlign: labelAlign.value,
    item: item.value,
    payload: payload.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_item = () => {
    return $fn.value.fetch('read_notification', {
      parameters: { id: props.id },
      asResource: true,
    });
  };
  const $$load_payload = () => {
    return {
      price: item.value.data.new_price,
      valid_from: item.value.data.source_object.valid_to || $fn.value.today(),
      valid_to: null,
      notes: null,
    };
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'labelAlign' },
      { name: 'item' },
      { name: 'payload', dependsOn: ['item'] },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: { item: $$load_item, payload: $$load_payload },
    ux: {},
    dataRefs: {
      labelAlign,
      item,
      payload,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return { title: $fn.value.gettext('Create new price') };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.id,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('id');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_mctduqq4p_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeModal_jw77zhq7b_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        close: true,
      },
    };
  };
  const act_1040167445267876_request_0t8tc5b13_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'update_notification',
      data: {
        id: item.value.id,
        $etag: item.value.$etag,
        state: 'handled',
        result_notes: payload.value.notes,
        result_data: payload.value,
      },
    };
  };
  const act_2434514588044777_closeModal_5r1gx5qt1_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
      },
    };
  };

  const act_7315092382398562_closeModal_mctduqq4p =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_mctduqq4p_getActionArgs,
    });
  const inst_evh_7315092382398562_close_u4k6zsrqp =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_mctduqq4p,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_u4k6zsrqp],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_s6i3i820g =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_p2fnwsbe8 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_s6i3i820g,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_p2fnwsbe8],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_or26u9qrs =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_q68588uhx =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_or26u9qrs,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_q68588uhx],
    event: 'close',
  });
  const act_1040167445267876_closeModal_jw77zhq7b =
    new actionTypes.CloseModalAction({
      actionArgs: act_1040167445267876_closeModal_jw77zhq7b_getActionArgs,
    });
  const inst_evh_1040167445267876_success_xyg1mg1a0 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeModal_jw77zhq7b,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_1040167445267876_request_0t8tc5b13 = new actionTypes.RequestAction({
    actionArgs: act_1040167445267876_request_0t8tc5b13_getActionArgs,
    displayName: 'update',
    events: [inst_evh_1040167445267876_success_xyg1mg1a0],
  });
  const inst_evh_1040167445267876_submit_a2x0chb3z =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_request_0t8tc5b13,
      event: 'submit',
      displayName: 'update',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_a2x0chb3z],
    event: 'submit',
  });
  const act_2434514588044777_closeModal_5r1gx5qt1 =
    new actionTypes.CloseModalAction({
      actionArgs: act_2434514588044777_closeModal_5r1gx5qt1_getActionArgs,
    });
  const inst_evh_2434514588044777_clickPreventStop_t1r6ckhdi =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeModal_5r1gx5qt1,
      event: 'click.prevent.stop',
      displayName: 'closeModal',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_t1r6ckhdi],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_u4k6zsrqp,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_p2fnwsbe8,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_q68588uhx,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_xyg1mg1a0,
    inst_evh_1040167445267876_submit_a2x0chb3z,
    update: inst_evh_1040167445267876_submit_a2x0chb3z,
    inst_evh_1040167445267876_submit,
    inst_evh_2434514588044777_clickPreventStop_t1r6ckhdi,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_u4k6zsrqp = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_u4k6zsrqp.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_p2fnwsbe8 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_p2fnwsbe8.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_q68588uhx = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_q68588uhx.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_xyg1mg1a0 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_xyg1mg1a0.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_a2x0chb3z = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_a2x0chb3z.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_t1r6ckhdi = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_t1r6ckhdi.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="InflationIndexedPriceChange"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        name="payload"
        id="hnvqxzl56c"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpFormGroup
          labelFor="contract_number"
          :label="gettext('Contract number')"
          :label-cols-md="4"
          :content-cols-md="8"
          :labelAlign="labelAlign"
        >
          <ItpFormPlaintext strong>
            {{ item.data.contract_data.contract_number }}
          </ItpFormPlaintext>
        </ItpFormGroup>
        <ItpFormGroup
          labelFor="tenant"
          :label="gettext('Tenant')"
          :label-cols-md="4"
          :content-cols-md="8"
          :labelAlign="labelAlign"
        >
          <ItpFormPlaintext strong>
            {{ item.data.contract_data.counterparty.name }}
          </ItpFormPlaintext>
        </ItpFormGroup>
        <ItpFormGroup
          labelFor="rental"
          :label="gettext('Rental')"
          :label-cols-md="4"
          :content-cols-md="8"
          :labelAlign="labelAlign"
          v-if="item.data.contract_data.rental"
        >
          <ItpFormPlaintext strong>
            {{ item.data.contract_data.rental.name }}
          </ItpFormPlaintext>
        </ItpFormGroup>
        <hr />
        <ItpFormGroup
          name="name"
          :label="pgettext('ContractService', 'Name')"
          :label-cols-md="4"
          :content-cols-md="8"
          :labelAlign="labelAlign"
        >
          <ItpFormInput
            name="name"
            v-model="item.data.service_data.name"
            readonly
          >
          </ItpFormInput>
        </ItpFormGroup>
        <ItpFormGroup
          name="current_price"
          :label="pgettext('ContractServiceFee', 'Current price')"
          :label-cols-md="4"
          :content-cols-md="8"
          :labelAlign="labelAlign"
          contentClass="input-group"
        >
          <ItpFormInput
            type="number"
            name="fee_amount"
            v-model="item.data.source_object.amount"
            readonly
            class="col-4"
          >
          </ItpFormInput>
          <span class="input-group-text">
            {{ item.data.source_object.currency }}
          </span>
        </ItpFormGroup>
        <ItpFormGroup
          name="amount"
          :label="pgettext('ContractServiceFee', 'New price')"
          :label-cols-md="4"
          :content-cols-md="8"
          :labelAlign="labelAlign"
          contentClass="input-group"
        >
          <ItpFormInput
            type="number"
            name="fee_amount"
            v-model="payload.price"
            required
            class="col-4"
          >
          </ItpFormInput>
          <span class="input-group-text">
            {{ item.data.source_object.currency }}
          </span>
        </ItpFormGroup>
        <ItpFormGroup
          name="valid_from"
          :label="pgettext('ContractServiceFee', 'Valid from')"
          :label-cols-md="4"
          :content-cols-md="4"
          :labelAlign="labelAlign"
        >
          <ItpFormDatetime
            name="valid_from"
            v-model="payload.valid_from"
            required
            :readonly="isServiceReadonly"
          >
          </ItpFormDatetime>
        </ItpFormGroup>
        <ItpFormGroup
          name="valid_to"
          :label="pgettext('ContractServiceFee', 'Valid to')"
          :label-cols-md="4"
          :content-cols-md="4"
          :labelAlign="labelAlign"
        >
          <ItpFormDatetime
            name="valid_to"
            v-model="payload.valid_to"
            :readonly="isServiceReadonly"
          >
          </ItpFormDatetime>
        </ItpFormGroup>
        <ItpFormGroup
          name="fee_notes"
          :label="pgettext('ContractServiceFee', 'Notes')"
          :label-cols-md="4"
          :content-cols-md="8"
          :labelAlign="labelAlign"
        >
          <ItpFormTextarea name="fee_notes" v-model="payload.notes">
          </ItpFormTextarea>
        </ItpFormGroup>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          form="hnvqxzl56c"
          :text="gettext('Save')"
          type="submit"
          variant="primary"
          :spinning="actions.update.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Cancel')"
          variant="default"
          :disabled="actions.update.isRunning"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
